import clsx from 'clsx';
import ThemeProvider from '@pixum/theme-provider';
import './styles.css';
import type { CardNode, ThemeNode } from 'data-layer';
import { DEVICES, LINK_TYPES } from 'data-layer/constants';
import { isLinkType } from 'utils';
import { isSwipeable } from '../../utils';
import { OrderWayCard } from './partials';
import type { DeviceOrientedComponentProps } from '../../types';

interface OrderWayCollectionProps extends DeviceOrientedComponentProps {
  orderWays: CardNode[];
  theme: ThemeNode;
  layout: string;
  inDialog?: boolean;
}

/**
 * Reorders order ways based on the device type to prioritize the most relevant option
 * Desktop: Software download first
 * Mobile: App download first
 */
function reorderOrderWaysByDevice(orderWays: CardNode[], device: string): CardNode[] {
  const isDesktop = device === DEVICES.DESKTOP;
  const isMobile = device === DEVICES.MOBILE;

  // Find the first card containing the device-appropriate download link
  const mainCardIndex = orderWays.findIndex(card =>
    card.links?.some(link =>
      isDesktop
        ? isLinkType(link, LINK_TYPES.PHOTOBOOK_SOFTWARE)
        : isMobile
          ? isLinkType(link, LINK_TYPES.DOWNLOAD_APP)
          : undefined
    )
  );

  const hasMainCard = mainCardIndex >= 0;

  // If main card exist, move it to the front
  if (hasMainCard) {
    const reorderedOrderWays = [...orderWays];
    const [mainCard] = reorderedOrderWays.splice(mainCardIndex, 1);
    reorderedOrderWays.unshift(mainCard);

    return reorderedOrderWays;
  }

  // Otherwise, return original order
  return orderWays;
}

/**
 * OrderWayCollection Component
 *
 * This component is responsible for rendering a collection of order way cards.
 * It utilizes the ThemeProvider to apply theming based on the provided theme prop.
 *
 * Props:
 * - orderWays: An array of CardNode objects representing the individual order way cards to be displayed.
 * - theme: A ThemeNode object that defines the theme to be applied to the component.
 * - layout: A string or ContentfulMobilePresentationTypes value that determines the layout and swipeability of the grid.
 * - inDialog: Boolean indicating if the component is rendered within a dialog (changes layout to show primary + secondary order ways).
 *
 * Note: When displayed in a dialog, the order ways are reordered by device type:
 * - Desktop: Software download is shown as primary
 * - Mobile: App download is shown as primary
 */
export function OrderWayCollection({ orderWays, theme, layout, device, inDialog = false }: OrderWayCollectionProps) {
  // Reorder order ways based on the device when in dialog
  const reorderedOrderWays = inDialog ? reorderOrderWaysByDevice(orderWays, device) : orderWays;
  const primaryOrderWay = reorderedOrderWays[0];
  const otherOrderWays = reorderedOrderWays.slice(1);

  return (
    <ThemeProvider theme={theme}>
      {inDialog ? (
        <div className="order-ways__dialog">
          {primaryOrderWay && (
            <div className="order-way--primary">
              <OrderWayCard
                card={primaryOrderWay}
                device={device}
                inDialog
                isFocused
                key={`OrderWayCard-${primaryOrderWay.id}`}
              />
            </div>
          )}
          <div className="order-way--others">
            {otherOrderWays.map(orderWay => (
              <OrderWayCard card={orderWay} device={device} inDialog key={`OrderWayCard-${orderWay.id}`} />
            ))}
          </div>
        </div>
      ) : (
        <div className={clsx('grid__wrapper', isSwipeable(layout) && 'grid__wrapper--swipeable')}>
          {orderWays.map(orderWay => (
            <OrderWayCard card={orderWay} device={device} key={`OrderWayCard-${orderWay.id}`} />
          ))}
        </div>
      )}
    </ThemeProvider>
  );
}
